/**
 * Legend Color Normalizer
 *
 * Single source of truth for converting ANY color format to valid 6-character hex
 * for PDF generation. This ensures colors are normalized at the source before reaching
 * the PDF generator, eliminating silent failures and invalid color issues.
 *
 * Created: 2026-02-09
 * Purpose: Fix legend color issues with 8-char hex, "transparent", and mixed formats
 */

/**
 * Validates if a string is a valid 6-character hex color
 * @param color - String to validate
 * @returns true if valid 6-char hex like "#RRGGBB"
 */
function isValid6CharHex(color: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
}

/**
 * Strips alpha channel from 8-character hex
 * @param hex - 8-character hex like "#RRGGBBAA"
 * @returns 6-character hex like "#RRGGBB"
 */
function stripAlphaChannel(hex: string): string {
  if (/^#[0-9A-Fa-f]{8}$/.test(hex)) {
    return hex.substring(0, 7); // Keep "#RRGGBB", remove "AA"
  }
  return hex;
}

/**
 * Expands 3-character hex shorthand to 6-character hex
 * @param hex - 3-character hex like "#RGB"
 * @returns 6-character hex like "#RRGGBB"
 */
function expandShorthandHex(hex: string): string {
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    const r = hex[1];
    const g = hex[2];
    const b = hex[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return hex;
}

/**
 * Converts rgb(r,g,b) or rgba(r,g,b,a) string to hex
 * @param rgbString - String like "rgb(255, 0, 0)" or "rgba(255, 0, 0, 0.5)"
 * @returns 6-character hex or null if parsing fails
 */
function rgbStringToHex(rgbString: string): string | null {
  // Match rgb(r, g, b) or rgba(r, g, b, a)
  const rgbMatch = rgbString.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);

  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);

    // Validate RGB values are in range
    if (r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255) {
      const toHex = (n: number) => n.toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
  }

  return null;
}

/**
 * Normalizes any color value to a valid 6-character hex string
 * This is THE ONLY function that needs to handle all edge cases
 *
 * @param color - Any color value (hex, rgb, rgba, "transparent", undefined, etc.)
 * @param defaultColor - Fallback color if normalization fails
 * @returns Always returns a valid 6-character hex string like "#RRGGBB"
 *
 * @example
 * normalizeColorForPDF('#ff0000') // '#ff0000'
 * normalizeColorForPDF('#000000ff') // '#000000' (strips alpha)
 * normalizeColorForPDF('#f0f') // '#ff00ff' (expands shorthand)
 * normalizeColorForPDF('rgb(255, 0, 0)') // '#ff0000'
 * normalizeColorForPDF('transparent') // '#666666' (uses default)
 * normalizeColorForPDF(undefined) // '#666666' (uses default)
 */
export function normalizeColorForPDF(
  color: string | undefined | null,
  defaultColor: string = '#666666'
): string {
  // Log input in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('[ColorNormalizer] Input:', color);
  }

  // Handle null/undefined
  if (!color) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ColorNormalizer] Output (null/undefined):', defaultColor);
    }
    return defaultColor;
  }

  // Trim whitespace
  const trimmed = color.trim();

  // Handle empty string
  if (trimmed === '') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ColorNormalizer] Output (empty):', defaultColor);
    }
    return defaultColor;
  }

  // Handle "transparent" - return default color
  if (trimmed.toLowerCase() === 'transparent') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ColorNormalizer] Output (transparent):', defaultColor);
    }
    return defaultColor;
  }

  // Handle hex colors
  if (trimmed.startsWith('#')) {
    // Already valid 6-char hex
    if (isValid6CharHex(trimmed)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ColorNormalizer] Output (valid 6-char hex):', trimmed);
      }
      return trimmed;
    }

    // 8-char hex with alpha - strip alpha
    if (/^#[0-9A-Fa-f]{8}$/.test(trimmed)) {
      const stripped = stripAlphaChannel(trimmed);
      if (process.env.NODE_ENV === 'development') {
        console.log('[ColorNormalizer] Output (8-char hex, stripped):', stripped);
      }
      return stripped;
    }

    // 3-char shorthand - expand
    if (/^#[0-9A-Fa-f]{3}$/.test(trimmed)) {
      const expanded = expandShorthandHex(trimmed);
      if (process.env.NODE_ENV === 'development') {
        console.log('[ColorNormalizer] Output (3-char hex, expanded):', expanded);
      }
      return expanded;
    }

    // Invalid hex format
    console.warn(`[ColorNormalizer] Invalid hex format: ${trimmed}, using default: ${defaultColor}`);
    return defaultColor;
  }

  // Handle rgb/rgba strings
  if (trimmed.toLowerCase().startsWith('rgb')) {
    const hexColor = rgbStringToHex(trimmed);
    if (hexColor) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ColorNormalizer] Output (rgb converted):', hexColor);
      }
      return hexColor;
    }

    // Invalid rgb format
    console.warn(`[ColorNormalizer] Invalid rgb format: ${trimmed}, using default: ${defaultColor}`);
    return defaultColor;
  }

  // Unrecognized format
  console.warn(`[ColorNormalizer] Unrecognized color format: ${trimmed}, using default: ${defaultColor}`);
  return defaultColor;
}

/**
 * Specifically handles fillColor with transparent support
 * Returns undefined for "transparent" to indicate no fill
 *
 * @param fillColor - Fill color value (can be "transparent")
 * @returns Valid 6-char hex or undefined for transparent
 *
 * @example
 * normalizeFillColor('#ff0000') // '#ff0000'
 * normalizeFillColor('transparent') // undefined
 * normalizeFillColor('#000000ff') // '#000000'
 * normalizeFillColor(undefined) // undefined
 */
export function normalizeFillColor(
  fillColor: string | undefined | null
): string | undefined {
  // Log input in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('[ColorNormalizer] FillColor Input:', fillColor);
  }

  // Handle null/undefined
  if (!fillColor) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ColorNormalizer] FillColor Output (null/undefined):', undefined);
    }
    return undefined;
  }

  const trimmed = fillColor.trim();

  // Handle empty string
  if (trimmed === '') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ColorNormalizer] FillColor Output (empty):', undefined);
    }
    return undefined;
  }

  // Handle "transparent" - return undefined (no fill)
  if (trimmed.toLowerCase() === 'transparent') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[ColorNormalizer] FillColor Output (transparent):', undefined);
    }
    return undefined;
  }

  // For any other color, normalize it
  const normalized = normalizeColorForPDF(trimmed, '#cccccc');

  if (process.env.NODE_ENV === 'development') {
    console.log('[ColorNormalizer] FillColor Output (normalized):', normalized);
  }

  return normalized;
}

/**
 * Utility function to validate if a color is already normalized
 * Useful for testing and debugging
 *
 * @param color - Color to check
 * @returns true if color is a valid 6-character hex
 */
export function isNormalizedColor(color: string | undefined): boolean {
  return color ? isValid6CharHex(color) : false;
}
